import { suggestedInventory } from '../data/inventory';
import type { PricingConfig } from '../data/pricingConfig';
import type { AccessInfo, Bedrooms, FunnelData, QuoteResult } from '../types/funnel';
import { evaluateQuote } from './quoteEngine';
import { buildSmsConsentRecord } from '../data/consent';

export interface TestResult {
  name: string;
  expected: string;
  actual: string;
  pass: boolean;
}

const acc = (p: Partial<AccessInfo> = {}): AccessInfo => ({
  floor: 'ground', flights: 0, method: 'stairs', elevatorReservation: 'unknown', sharedElevator: 'unknown',
  truckNearEntrance: 'yes', carryBand: '0_20', carryFeet: '', parking: 'driveway', innerCarryBand: '0_20', restrictions: '', loadingHours: '',
  gateAccess: 'no', coiRequired: 'no', notes: '', ...p,
});

function base(bedrooms: Bedrooms, p: Partial<FunnelData> = {}): FunnelData {
  return {
    homeType: 'apartment', bedrooms, originZip: '33602', originStreet: '', originUnit: '', destZip: '33606', destStreet: '', destUnit: '',
    originBuilding: '', destBuilding: '', originBuildingNo: '', destBuildingNo: '', originCity: 'Tampa', originState: 'FL', originPlaceId: null, originAddrStatus: 'typed', destCity: 'Tampa', destState: 'FL', destPlaceId: null, destAddrStatus: 'typed',
    stops: [], officeSize: null, itemNotes: {}, truckPreference: null,
    services: { reassembly: false, placement: false, garageOnly: false, garageOnlyNote: '', unpacking: false },
    origin: acc(), destination: acc(), inventory: { ...suggestedInventory[bedrooms] }, customItems: [], inventoryUnsure: false,
    needsDisassembly: false, specialItems: [], inventoryNotes: '', packing: 'self', packingProducts: {}, moveDate: '2026-11-02',
    dateUnsure: false, arrival: '8am', arrivalOther: '', firstName: 'Test', lastName: 'Case', phone: '8135550100', email: 't@example.com',
    commLang: 'en', contactChannel: 'call', consentQuote: true, consentSmsMarketing: false, consentSmsNonMarketing: false, consentAiCall: false, crewPreference: null,
    acceptedQuoteHash: null, thirdPartyPayer: false, payerName: '', payerEmail: '', payerPhone: '', paymentMethod: 'card', ...p,
  };
}

/** Runs the real quote engine in the browser. These are logic tests, not integration tests. */
export function runEngineTests(cfg: PricingConfig): TestResult[] {
  const out: TestResult[] = [];
  const add = (name: string, expected: string, actual: string, pass: boolean) => out.push({ name, expected, actual, pass });
  const crew = (name: string, d: FunnelData, n: number) => {
    const q = evaluateQuote(d, cfg);
    add(name, `recommend ${n}`, `recommend ${q.crew.recommended}`, q.crew.recommended === n);
  };

  crew('1 BR ground', base('1'), 2);
  crew('1 BR 3rd-floor stairs', base('1', { origin: acc({ floor: 'third', flights: 2 }) }), 2);
  crew('2 BR 2nd floor', base('2', { origin: acc({ floor: 'second', flights: 1 }) }), 2);
  crew('2 BR → 3rd-floor stairs', base('2', { destination: acc({ floor: 'third', flights: 2 }) }), 3);
  crew('3 BR ground', base('3', { homeType: 'house' }), 3);
  crew('4 BR house', base('4', { homeType: 'house' }), 3);

  const pref = evaluateQuote(base('3', { crewPreference: 2 }), cfg);
  add('3 BR customer prefers 2 movers', 'used 2 + review flag', `used ${pref.crew.used}; ${pref.reviewReasons.join(', ') || 'none'}`, pref.crew.used === 2 && pref.reviewReasons.includes('rr_crewPref'));

  const min = evaluateQuote(base('few', { inventory: { box_small: 2 } }), cfg);
  add('Few items → 4 h minimum', `≥ ${cfg.minimumServiceHours} h`, `${min.serviceLow} h`, min.serviceLow >= cfg.minimumServiceHours);

  const ex = evaluateQuote(base('few', { inventory: { box_small: 1 } }), cfg);
  add('Minimum labor = $129 × (4 + 1 travel)', '$645', ex.laborLow !== null ? `$${ex.laborLow}` : 'n/a', ex.laborLow === cfg.rates[2] * (cfg.minimumServiceHours + cfg.travelHours));

  add('Surcharge not calculated without approved base', 'not approved', ex.surchargeBaseApproved ? 'approved' : 'not approved', !ex.surchargeBaseApproved);
  add('Deposit credited once', 'remaining = labor − 99', ex.laborLow !== null && ex.remainingLow !== null ? `${ex.laborLow} − 99 = ${ex.remainingLow}` : 'n/a', ex.remainingLow === (ex.laborLow ?? 0) - cfg.depositAmount);

  const miami1 = evaluateQuote(base('1', { destZip: '33131' }), cfg);
  add('Tampa → Miami 1 BR: no automatic booking', 'review', miami1.zone, miami1.zone === 'review');
  const miami3 = evaluateQuote(base('3', { destZip: '33131' }), cfg);
  add('Tampa → Miami 3 BR: eligible + transport estimate', 'eligible_prelim + transport', `${miami3.zone}${miami3.needsTransport ? ' + transport' : ''}`, miami3.zone === 'eligible_prelim' && miami3.needsTransport);
  const orlando = evaluateQuote(base('1', { destZip: '32801' }), cfg);
  add('Tampa → Orlando 1 BR: nearby', 'eligible_prelim', orlando.zone, orlando.zone === 'eligible_prelim');
  const naples1 = evaluateQuote(base('1', { destZip: '34102' }), cfg);
  add('Tampa → Naples 1 BR: corridor needs 2+ BR', 'review', naples1.zone, naples1.zone === 'review');
  const tally = evaluateQuote(base('3', { destZip: '32301' }), cfg);
  add('Tampa → Tallahassee: never automatic', 'outside', tally.zone, tally.zone === 'outside');
  const stopPanhandle = evaluateQuote(base('2', { stops: [{ id: 's1', street: '', unit: '', building: '', buildingNo: '', city: '', state: 'FL', zip: '32401', placeId: null, status: 'typed', role: 'pickup', items: '', access: acc() }] }), cfg);
  add('Extra stop in Panama City blocks automatic booking', 'outside', stopPanhandle.zone, stopPanhandle.zone === 'outside');
  const card = evaluateQuote(base('few', { inventory: { box_small: 1 } }), cfg);
  add('4% card charge on labor', `${(cfg.rates[2] * 5 * 0.04).toFixed(2)}`, card.cardFeeLow !== null ? `${card.cardFeeLow.toFixed(2)}` : 'n/a', card.cardFeeLow === Math.round(cfg.rates[2] * 5 * cfg.cardFeePercent) / 100);

  const high = evaluateQuote(base('3', { origin: acc({ floor: 'fourth_plus', method: 'freight_elevator', carryBand: 'over_100' }) }), cfg);
  add('3 BR high-rise + long carry → review', 'rr_elevator + rr_longCarry', high.reviewReasons.join(', '), high.reviewReasons.includes('rr_elevator') && high.reviewReasons.includes('rr_longCarry'));

  const piano = evaluateQuote(base('2', { specialItems: ['piano'] }), cfg);
  add('Piano → review', 'rr_special', piano.reviewReasons.join(', ') || 'none', piano.reviewReasons.includes('rr_special'));

  const pack = evaluateQuote(base('2', { packing: 'professional', packingProducts: { packed_10: 1 } }), cfg);
  add('Unapproved packing never priced', 'priced by team', pack.packingPriced ? 'priced' : 'priced by team', !pack.packingPriced);

  const a = evaluateQuote(base('1'), cfg);
  const b = evaluateQuote(base('1', { inventory: { ...suggestedInventory['1'], sofa: 2 } }), cfg);
  add('Inventory edit makes accepted estimate stale', 'hash changes', a.hash !== b.hash ? 'changed' : 'same', a.hash !== b.hash);

  // SMS consent: every combination is stored independently; nothing else sets it.
  const combos: [boolean, boolean][] = [
    [false, false],
    [true, false],
    [false, true],
    [true, true],
  ];
  for (const [m, n] of combos) {
    const r = buildSmsConsentRecord(m, n, 'en');
    add(`SMS consent marketing=${m} non-marketing=${n}`, `${m}/${n}`, `${r.sms_marketing_consent}/${r.sms_nonmarketing_consent}`, r.sms_marketing_consent === m && r.sms_nonmarketing_consent === n);
  }
  const fresh = base('1', { phone: '(813) 555-0100', contactChannel: 'text', consentQuote: true, consentAiCall: true });
  const freshRec = buildSmsConsentRecord(fresh.consentSmsMarketing, fresh.consentSmsNonMarketing, 'es');
  add('Phone + "text" channel + other consents ≠ SMS consent', 'false/false', `${freshRec.sms_marketing_consent}/${freshRec.sms_nonmarketing_consent}`, !freshRec.sms_marketing_consent && !freshRec.sms_nonmarketing_consent);
  add('Consent text version', '2026-10-06', freshRec.consent_text_version, freshRec.consent_text_version === '2026-10-06');
  add('Server sets timestamp (browser sends none)', 'null', String(freshRec.consent_timestamp), freshRec.consent_timestamp === null);

  return out;
}
